import React, { useState, useEffect } from 'react'
import { Client, CreateClientDto, Group, UpdateClientDto } from '@/types'
import { validateEmail, validateIban, validatePassword, validateSiret } from '@/utils/validation'
import { Modal } from '@/components/ui/Modal'
import { FormStepper } from '@/components/ui/FormStepper'
import { Input } from '@/components/ui/Input'
import { Checkbox } from '@/components/ui/Checkbox'
import { Select } from '@/components/ui/Select'
import { PLACE_OPTIONS_ARRAY } from '@/config/place.enum'
import { useClientsStore } from '@/stores/clients.store'
import { useGroupsStore } from '@/stores/groups.store'
import { useAuthStore } from '@/stores/auth.store'
import { isDirectClient } from '@/utils/client-scope'
import { toastError, toastSuccess } from '@/utils/toast'

const FORM_STEPS = [
  'Groupe & identité',
  'Contact & accès',
  'Adresse',
  'Facturation',
  'Professionnel',
] as const

interface ClientFormModalProps {
  client: Client | null
  onClose: () => void
  onSuccess: () => void
  onUpdateClient?: (id: string, data: UpdateClientDto) => Promise<Client>
}

export const ClientFormModal: React.FC<ClientFormModalProps> = ({
  client,
  onClose,
  onSuccess,
  onUpdateClient,
}) => {
  const groups = useGroupsStore(state => state.groups)
  const addClient = useClientsStore(state => state.addClient)
  const updateClientFromStore = useClientsStore(state => state.updateClient)
  const updateClient = onUpdateClient ?? updateClientFromStore
  const isAdmin = useAuthStore(state => state.isAdmin)

  const isDirect = client ? isDirectClient(client) : isAdmin

  const isEditMode = !!client

  const [currentStep, setCurrentStep] = useState(0)
  const [highestStepReached, setHighestStepReached] = useState(0)
  const [formData, setFormData] = useState<CreateClientDto>({
    group: '',
    firstname: '',
    lastname: '',
    name: '',
    email: '',
    password: '',
    phone: '',
    noPS: '',
    address: '',
    city: '',
    postalCode: '',
    siret: '',
    accountOwner: '',
    iban: '',
    bic: '',
    place: '01' as CreateClientDto['place'],
    autGIE: false,
    autPublic: false,
  })

  const [groupSearch, setGroupSearch] = useState('')
  const [showGroupDropdown, setShowGroupDropdown] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    setCurrentStep(0)
    setHighestStepReached(isEditMode ? FORM_STEPS.length - 1 : 0)
    if (client) {
      const groupId = typeof client.group === 'string' ? client.group : client.group._id
      const group = groups?.find(g => g._id === groupId)
      setFormData({
        group: groupId,
        firstname: client.firstname,
        lastname: client.lastname,
        name: client.name,
        email: client.email,
        password: '',
        phone: client.phone || '',
        noPS: client.noPS,
        address: client.address,
        city: client.city,
        postalCode: client.postalCode,
        siret: client.siret || '',
        accountOwner: client.accountOwner || '',
        iban: client.iban || '',
        bic: client.bic || '',
        place: client.place,
        autGIE: client.autGIE,
        autPublic: client.autPublic,
      })
      setGroupSearch(group?.name || '')
    }
  }, [client, groups, isEditMode])

  const filteredGroups = groups?.filter(group =>
    group?.name.toLowerCase().includes(groupSearch.toLowerCase())
  )

  const handleGroupSelect = (group: Group) => {
    setFormData(prev => ({ ...prev, group: group._id }))
    setGroupSearch(group.name)
    setShowGroupDropdown(false)
  }

  const handleGroupInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setGroupSearch(e.target.value)
    setShowGroupDropdown(true)
    const selectedGroup = groups?.find(g => g._id === formData.group)
    if (selectedGroup && !selectedGroup.name.toLowerCase().includes(e.target.value.toLowerCase())) {
      setFormData(prev => ({ ...prev, group: '' }))
      setErrors(prev => {
        const newErrors = { ...prev }
        if (newErrors.group) delete newErrors.group
        return newErrors
      })
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target
    const checked = (e.target as HTMLInputElement).checked

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }))
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[name]
        return newErrors
      })
    }
  }

  const handlePlaceChange = (value: string) => {
    setFormData(prev => ({
      ...prev,
      place: value as CreateClientDto['place'],
    }))
    if (errors.place) {
      setErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors.place
        return newErrors
      })
    }
  }

  const canNavigateToStep = (step: number) => isEditMode || step <= highestStepReached

  const handleStepClick = (step: number) => {
    if (!canNavigateToStep(step)) return
    setCurrentStep(step)
  }

  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {}

    if (step === 0) {
      if (!formData.group) newErrors.group = 'Le groupe est requis'
      if (!formData.firstname.trim()) newErrors.firstname = 'Le prénom est requis'
      if (!formData.lastname.trim()) newErrors.lastname = 'Le nom est requis'
      if (!formData.name.trim()) newErrors.name = 'La raison sociale est requise'
    }

    if (step === 1) {
      if (!formData.email.trim()) {
        newErrors.email = "L'email est requis"
      } else if (!validateEmail(formData.email)) {
        newErrors.email = 'Email invalide'
      }
      if (!client && !formData.password) {
        newErrors.password = 'Le mot de passe est requis'
      } else if (formData.password && !validatePassword(formData.password)) {
        newErrors.password =
          'Le mot de passe doit contenir au moins 8 caractères, une majuscule, une minuscule, un chiffre et un caractère spécial'
      }
    }

    if (step === 2) {
      if (!formData.address.trim()) newErrors.address = "L'adresse est requise"
      if (!formData.city.trim()) newErrors.city = 'La ville est requise'
      if (!formData.postalCode.trim()) newErrors.postalCode = 'Le code postal est requis'
      if (!formData.place) newErrors.place = 'Le lieu est requis'
    }

    if (step === 3) {
      const siret = formData.siret?.trim() ?? ''
      if (isDirect && !siret) {
        newErrors.siret = 'Le SIRET est requis'
      } else if (siret && !validateSiret(siret)) {
        newErrors.siret = 'Le SIRET doit contenir 14 chiffres'
      }
      const iban = formData.iban?.trim() ?? ''
      if (iban && !validateIban(iban)) newErrors.iban = 'IBAN invalide'
      if (iban && !formData.accountOwner?.trim()) {
        newErrors.accountOwner = 'Le titulaire du compte est requis avec un IBAN'
      }
    }

    if (step === 4) {
      if (!formData.noPS.trim()) newErrors.noPS = 'Le numéro PS est requis'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const validate = (): boolean => {
    for (let step = 0; step < FORM_STEPS.length; step++) {
      if (!validateStep(step)) {
        setCurrentStep(step)
        return false
      }
    }
    return true
  }

  const handleNext = () => {
    if (!validateStep(currentStep)) return
    const nextStep = Math.min(currentStep + 1, FORM_STEPS.length - 1)
    setHighestStepReached(prev => Math.max(prev, nextStep))
    setCurrentStep(nextStep)
  }

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 0))
  }

  const handleSubmit = async () => {
    if (!validate()) return

    setLoading(true)
    try {
      const dataToSend: CreateClientDto | UpdateClientDto = { ...formData }
      if (client && !dataToSend.password) {
        delete (dataToSend as UpdateClientDto).password
      }
      if (!dataToSend.siret?.trim()) {
        delete dataToSend.siret
      }
      if (!dataToSend.accountOwner?.trim()) delete dataToSend.accountOwner
      if (dataToSend.iban?.trim()) {
        dataToSend.iban = dataToSend.iban.replace(/\s/g, '')
      } else {
        delete dataToSend.iban
      }
      if (!dataToSend.bic?.trim()) delete dataToSend.bic

      if (client) {
        await updateClient(client._id, dataToSend as UpdateClientDto)
        toastSuccess('Client modifié avec succès')
      } else {
        await addClient(dataToSend as CreateClientDto)
        toastSuccess('Client créé avec succès')
      }
      onSuccess()
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : "Erreur lors de l'enregistrement du client"
      toastError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const isLastStep = currentStep === FORM_STEPS.length - 1
  const submitLabel = isEditMode ? 'Modifier' : 'Créer'

  return (
    <Modal
      title={client ? 'Modifier le client' : 'Nouveau client'}
      confirmButtonText={isLastStep ? submitLabel : 'Suivant'}
      onCancel={onClose}
      onConfirm={isLastStep ? handleSubmit : handleNext}
      onBack={currentStep > 0 ? handleBack : undefined}
      secondaryAction={
        isEditMode && !isLastStep ? { text: submitLabel, onClick: handleSubmit } : undefined
      }
      isLoading={loading}
    >
      <form
        onSubmit={e => {
          e.preventDefault()
          if (isLastStep) handleSubmit()
          else handleNext()
        }}
        className="space-y-6"
      >
        <FormStepper
          steps={[...FORM_STEPS]}
          currentStep={currentStep}
          canNavigateToStep={canNavigateToStep}
          onStepClick={handleStepClick}
        />

        {currentStep === 0 && (
          <>
            <div className="relative">
              <label className="block text-sm font-medium text-[var(--color-text-primary)] mb-2">
                Groupe <span className="text-[var(--color-error)]">*</span>
              </label>
              <Input
                type="text"
                value={groupSearch}
                onChange={handleGroupInputChange}
                onFocus={() => setShowGroupDropdown(true)}
                onBlur={() => {
                  setTimeout(() => setShowGroupDropdown(false), 200)
                }}
                placeholder="Rechercher un groupe..."
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[var(--color-primary)] focus:border-[var(--color-primary)] transition-colors ${
                  errors.group ? 'border-[var(--color-error)]' : 'border-[var(--color-border)]'
                }`}
              />
              {errors.group && (
                <p className="mt-1 text-sm text-[var(--color-error)]">{errors.group}</p>
              )}
              {showGroupDropdown && filteredGroups && filteredGroups.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-[var(--color-border)] rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {filteredGroups?.map(group => (
                    <button
                      key={group._id}
                      type="button"
                      onClick={() => handleGroupSelect(group)}
                      className="w-full text-left px-4 py-2 hover:bg-[var(--color-gray-100)] transition-colors"
                    >
                      {group.name}
                    </button>
                  ))}
                </div>
              )}
              {showGroupDropdown && filteredGroups && filteredGroups.length === 0 && groupSearch && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-[var(--color-border)] rounded-lg shadow-lg px-4 py-2 text-[var(--color-text-muted)]">
                  Aucun groupe trouvé
                </div>
              )}
              <input type="hidden" name="group" value={formData.group} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Prénom"
                type="text"
                name="firstname"
                value={formData.firstname}
                onChange={handleChange}
                error={errors.firstname}
                required
              />
              <Input
                label="Nom"
                type="text"
                name="lastname"
                value={formData.lastname}
                onChange={handleChange}
                error={errors.lastname}
                required
              />
            </div>

            <Input
              label="Raison sociale"
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              error={errors.name}
              required
            />
          </>
        )}

        {currentStep === 1 && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Email"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                error={errors.email}
                required
              />
              <Input
                label="Téléphone"
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
              />
            </div>

            <Input
              label="Mot de passe"
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              error={errors.password}
              required={!client}
              helperText={client ? '(Laisser vide pour ne pas modifier)' : undefined}
            />
          </>
        )}

        {currentStep === 2 && (
          <>
            <Input
              label="Adresse"
              type="text"
              name="address"
              value={formData.address}
              onChange={handleChange}
              error={errors.address}
              required
            />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Input
                label="Ville"
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
                error={errors.city}
                required
              />
              <Input
                label="Code postal"
                type="text"
                name="postalCode"
                value={formData.postalCode}
                onChange={handleChange}
                error={errors.postalCode}
                required
              />
              <Select
                label="Lieu"
                name="place"
                value={formData.place}
                onChange={handlePlaceChange}
                options={PLACE_OPTIONS_ARRAY}
                error={errors.place}
                required
              />
            </div>
          </>
        )}

        {currentStep === 3 && (
          <>
            <Input
              label="SIRET"
              type="text"
              name="siret"
              value={formData.siret ?? ''}
              onChange={handleChange}
              error={errors.siret}
              required={isDirect}
              placeholder="14 chiffres"
              helperText="Requis pour créer la fiche de facturation Flux du client direct"
            />

            <Input
              label="Titulaire du compte"
              type="text"
              name="accountOwner"
              value={formData.accountOwner ?? ''}
              onChange={handleChange}
              error={errors.accountOwner}
              helperText="Coordonnées bancaires (RIB) — facultatif"
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="IBAN"
                type="text"
                name="iban"
                value={formData.iban ?? ''}
                onChange={handleChange}
                error={errors.iban}
                placeholder="FR76 ..."
              />
              <Input
                label="BIC"
                type="text"
                name="bic"
                value={formData.bic ?? ''}
                onChange={handleChange}
                error={errors.bic}
              />
            </div>

            {client && isDirectClient(client) && client.fluxId != null && (
              <Input
                label="Identifiant Flux"
                type="text"
                name="fluxIdDisplay"
                value={String(client.fluxId)}
                disabled
                helperText="Créé automatiquement dans Flux lors de la création ou mise à jour du client direct"
              />
            )}
          </>
        )}

        {currentStep === 4 && (
          <>
            <Input
              label="Numéro PS"
              type="text"
              name="noPS"
              value={formData.noPS}
              onChange={handleChange}
              error={errors.noPS}
              required
            />

            <Checkbox
              label="Autorisation de communication des informations ci-dessus par le gestionnaire du GIE SESAM-Vitale"
              name="autGIE"
              checked={formData.autGIE}
              onChange={handleChange}
            />
            <Checkbox
              label="Autorisation de diffusion au public par quelque moyen que ce soit, des renseignements nécessaires à l'information relative au lieu d'implantation de l'équipement de télémise à jour par le GIE SESAM-Vitale et les organismes d'assurance maladie"
              name="autPublic"
              checked={formData.autPublic}
              onChange={handleChange}
            />
          </>
        )}
      </form>
    </Modal>
  )
}
